import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const APP_USERS_COLLECTION = 'appUsers';
const ORGS_COLLECTION = 'orgs';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Org {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  country: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  isDisabled: boolean;
  plan: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface OrgMember {
  uid: string;
  role: string;
  permissions: string[];
  isDisabled: boolean;
  displayName: string;
  email: string;
  lastSignIn: Timestamp;
  createdAt: Timestamp;
  avatarColor?: string;
  customAvatarColor?: string;
}

export interface OrgMembership {
  orgId: string;
  orgName: string;
  orgSlug: string;
  joinedAt: Timestamp;
}

/** Combined view used by OrgChooserPage — membership + member role. */
export interface OrgChooserEntry {
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: string;
}

export interface CreateOrgInput {
  name: string;
  slug: string;
  country: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  logoUrl?: string;
  creatorUid: string;
  creatorDisplayName: string;
  creatorEmail: string;
}

export interface OrgServiceError {
  code: string;
  message: string;
}

// ─── Slug helpers ─────────────────────────────────────────────────────────────

const SLUG_REGEX = /^[a-z][a-z0-9-]{2,5}$/;

/** Returns true if the slug matches the format: 3–6 chars, lowercase letters, digits, hyphens, starts with a letter. */
export function isValidSlug(slug: string): boolean {
  return SLUG_REGEX.test(slug);
}

const ADMIN_PERMISSIONS = [
  'customers.read', 'customers.write', 'customers.delete',
  'cropTypes.manage', 'regions.manage', 'seasonTimes.manage',
  'cropStatus.read', 'users.manage',
];

const OPERATOR_PERMISSIONS = [
  'customers.read', 'customers.write', 'cropStatus.read',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToOrg(id: string, data: DocumentData): Org {
  return {
    id,
    name: data.name as string,
    slug: data.slug as string,
    logoUrl: data.logoUrl as string | undefined,
    country: data.country as string,
    contactName: data.contactName as string,
    contactPhone: data.contactPhone as string,
    contactEmail: data.contactEmail as string,
    isDisabled: (data.isDisabled as boolean) ?? false,
    plan: (data.plan as string) ?? 'free',
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
  };
}

function docToOrgMember(uid: string, data: DocumentData): OrgMember {
  const member: OrgMember = {
    uid,
    role: data.role as string,
    permissions: (data.permissions ?? []) as string[],
    isDisabled: (data.isDisabled as boolean) ?? false,
    displayName: data.displayName as string,
    email: data.email as string,
    lastSignIn: data.lastSignIn as Timestamp,
    createdAt: data.createdAt as Timestamp,
    avatarColor: (data.avatarColor as string) ?? undefined,
    customAvatarColor: (data.customAvatarColor as string) ?? undefined,
  };
  return member;
}

function docToOrgMembership(data: DocumentData): OrgMembership {
  return {
    orgId: data.orgId as string,
    orgName: data.orgName as string,
    orgSlug: data.orgSlug as string,
    joinedAt: data.joinedAt as Timestamp,
  };
}

function toServiceError(error: unknown): OrgServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Org functions ────────────────────────────────────────────────────────────

/** Looks up an org by its URL slug. Returns null if not found. */
export async function getOrgBySlug(slug: string): Promise<Org | null> {
  try {
    const q = query(collection(db, ORGS_COLLECTION), where('slug', '==', slug));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return docToOrg(d.id, d.data());
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns true if the slug is not yet taken in the orgs collection. */
export async function isSlugAvailable(slug: string): Promise<boolean> {
  try {
    const q = query(collection(db, ORGS_COLLECTION), where('slug', '==', slug));
    const snap = await getDocs(q);
    return snap.empty;
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Creates a new org with seeded admin/operator roles and adds the creator as admin.
 * All writes are committed as a single batch. Returns the new org's slug.
 */
export async function createOrg(input: CreateOrgInput): Promise<string> {
  try {
    const orgRef = doc(collection(db, ORGS_COLLECTION));
    const orgId = orgRef.id;
    const batch = writeBatch(db);
    const now = serverTimestamp();

    batch.set(orgRef, {
      name: input.name,
      slug: input.slug,
      country: input.country,
      contactName: input.contactName,
      contactPhone: input.contactPhone,
      contactEmail: input.contactEmail,
      logoUrl: input.logoUrl ?? null,
      isDisabled: false,
      plan: 'free',
      createdBy: input.creatorUid,
      createdAt: now,
      updatedAt: now,
    });

    batch.set(doc(db, ORGS_COLLECTION, orgId, 'roles', 'admin'), {
      name: 'Admin',
      permissions: ADMIN_PERMISSIONS,
      isSystem: true,
      createdAt: now,
    });

    batch.set(doc(db, ORGS_COLLECTION, orgId, 'roles', 'operator'), {
      name: 'Operator',
      permissions: OPERATOR_PERMISSIONS,
      isSystem: true,
      createdAt: now,
    });

    batch.set(doc(db, ORGS_COLLECTION, orgId, 'members', input.creatorUid), {
      role: 'admin',
      permissions: ADMIN_PERMISSIONS,
      isDisabled: false,
      displayName: input.creatorDisplayName,
      email: input.creatorEmail,
      createdAt: now,
      lastSignIn: now,
    });

    batch.set(doc(db, APP_USERS_COLLECTION, input.creatorUid, 'memberships', orgId), {
      orgId,
      orgName: input.name,
      orgSlug: input.slug,
      joinedAt: now,
    });

    await batch.commit();
    return input.slug;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns all org memberships for a user from appUsers/{uid}/memberships. */
export async function getUserMemberships(uid: string): Promise<OrgMembership[]> {
  try {
    const ref = collection(db, APP_USERS_COLLECTION, uid, 'memberships');
    const snap = await getDocs(ref);
    return snap.docs.map((d) => docToOrgMembership(d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns combined org+role entries for the org chooser page.
 * Reads memberships subcollection, then fan-outs to member docs for role.
 */
export async function getAllOrgsForUser(uid: string): Promise<OrgChooserEntry[]> {
  try {
    const memberships = await getUserMemberships(uid);
    const results = await Promise.all(
      memberships.map(async (m) => {
        const member = await getOrgMember(m.orgId, uid);
        return {
          orgId: m.orgId,
          orgName: m.orgName,
          orgSlug: m.orgSlug,
          role: member?.role ?? 'operator',
        };
      }),
    );
    return results;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Gets a single member doc from orgs/{orgId}/members/{uid}. Returns null if not found. */
export async function getOrgMember(orgId: string, uid: string): Promise<OrgMember | null> {
  try {
    const ref = doc(db, ORGS_COLLECTION, orgId, 'members', uid);
    const snap = await getDoc(ref);
    return snap.exists() ? docToOrgMember(snap.id, snap.data()) : null;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Gets all members in an org. */
export async function getOrgMembers(orgId: string): Promise<OrgMember[]> {
  try {
    const snap = await getDocs(collection(db, ORGS_COLLECTION, orgId, 'members'));
    return snap.docs.map(d => docToOrgMember(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Disable or enable a member. */
export async function setMemberDisabled(orgId: string, uid: string, isDisabled: boolean): Promise<void> {
  try {
    const ref = doc(db, ORGS_COLLECTION, orgId, 'members', uid);
    await updateDoc(ref, { isDisabled });
  } catch (error) {
    throw toServiceError(error);
  }
}

export interface UpdateOrgInput {
  name: string;
  logoUrl?: string;
  country: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

/** Updates org fields and sets updatedAt to serverTimestamp(). */
export async function updateOrg(orgId: string, input: UpdateOrgInput): Promise<void> {
  try {
    const ref = doc(db, ORGS_COLLECTION, orgId);
    await updateDoc(ref, {
      name: input.name,
      logoUrl: input.logoUrl ?? null,
      country: input.country,
      contactName: input.contactName,
      contactPhone: input.contactPhone,
      contactEmail: input.contactEmail,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Super admin: fetch all orgs with member counts.
 * Used by the Super Admin Dashboard.
 */
export async function getAllOrgs(): Promise<(Org & { memberCount: number })[]> {
  try {
    const snap = await getDocs(collection(db, ORGS_COLLECTION));
    return Promise.all(
      snap.docs.map(async (orgDoc) => {
        const org = docToOrg(orgDoc.id, orgDoc.data());
        const membersSnap = await getDocs(
          collection(db, ORGS_COLLECTION, orgDoc.id, 'members')
        );
        return {
          ...org,
          memberCount: membersSnap.size,
        };
      })
    );
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Super admin: disable or enable an org. */
export async function setOrgDisabled(orgId: string, isDisabled: boolean): Promise<void> {
  try {
    const ref = doc(db, ORGS_COLLECTION, orgId);
    await updateDoc(ref, { isDisabled, updatedAt: serverTimestamp() });
  } catch (error) {
    throw toServiceError(error);
  }
}
